<template>
  <b-modal
    :visible="visible"
    title="Envolvidos da Ocorrência"
    size="lg"
    centered
    no-close-on-backdrop
    no-close-on-esc
    hide-footer
    @update:visible="$emit('update:visible', $event)"
    @hidden="$emit('close')"
    @shown="onShown"
  >
    <!-- Lista de envolvidos já adicionados -->
    <div
      v-if="involveds.length > 0"
      class="mb-3"
    >
      <h6 class="mb-2">
        <feather-icon
          icon="UsersIcon"
          size="15"
          class="mr-25"
        />
        Envolvidos vinculados
        <span class="badge badge-primary ml-1">{{ involveds.length }}</span>
      </h6>
      <div class="table-responsive">
        <table class="table table-sm table-bordered mb-0">
          <thead class="thead-light">
            <tr>
              <th>Natureza</th>
              <th>Papel</th>
              <th>Identificação</th>
              <th
                class="text-center"
                style="width: 60px"
              >
                Remover
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(inv, idx) in involveds"
              :key="idx"
            >
              <td>{{ inv.entity_type_label }}</td>
              <td>{{ inv.role_label || '—' }}</td>
              <td>{{ inv.display_label || '—' }}</td>
              <td class="text-center">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  @click="remove(idx)"
                >
                  <feather-icon
                    icon="TrashIcon"
                    size="13"
                  />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <hr>
    </div>

    <h6 class="mb-2">
      <feather-icon
        icon="UserPlusIcon"
        size="15"
        class="mr-25"
      />
      Adicionar envolvido
    </h6>

    <!-- 1. Natureza -->
    <div class="form-group">
      <label class="font-weight-bold mb-1">Natureza</label>
      <div
        v-if="loadingTypes"
        class="text-muted small"
      >
        <b-spinner small /> Carregando...
      </div>
      <div
        v-else
        class="btn-group-toggle d-flex flex-wrap"
        style="gap: 8px"
      >
        <button
          v-for="t in selectableTypes"
          :key="t.code"
          type="button"
          class="btn btn-sm"
          :class="entityType === t.code ? 'btn-primary' : 'btn-outline-secondary'"
          @click="selectType(t.code)"
        >
          <feather-icon
            v-if="t.icon"
            :icon="t.icon"
            size="14"
            class="mr-25"
          />
          {{ t.label }}
        </button>
      </div>
    </div>

    <!-- 2. Papel -->
    <div class="form-group">
      <label class="font-weight-bold mb-1">Papel</label>
      <select
        v-model="roleId"
        class="form-control"
        :disabled="loadingRoles"
      >
        <option :value="null">
          Selecione o papel
        </option>
        <option
          v-for="r in roles"
          :key="r.id"
          :value="r.id"
        >
          {{ r.label }}
        </option>
      </select>
    </div>

    <hr>

    <!-- Indicador de cadastro existente vinculado (preenchido automaticamente) -->
    <div
      v-if="reuse"
      class="alert alert-success py-1 px-2 d-flex justify-content-between align-items-center"
    >
      <span class="small">
        <feather-icon
          icon="CheckCircleIcon"
          size="14"
          class="mr-25"
        />
        Cadastro existente vinculado: <strong>{{ reuse.display_label }}</strong>
      </span>
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary"
        @click="clearReuse"
      >
        Limpar
      </button>
    </div>
    <div
      v-else-if="lookupLoading"
      class="text-muted small mb-2"
    >
      <b-spinner small /> Buscando cadastro...
    </div>

    <component
      :is="formComponent"
      v-model="activeForm"
      :errors="errors"
      :disabled="!!reuse"
      @lookup-key="onKeyLookup"
    />

    <div
      class="d-flex justify-content-between align-items-center mt-2 pt-2 inv-footer"
    >
      <button
        type="button"
        class="btn btn-outline-secondary"
        @click="$emit('update:visible', false)"
      >
        Fechar
      </button>
      <button
        type="button"
        class="btn btn-primary"
        @click="addInvolved"
      >
        <feather-icon
          icon="PlusIcon"
          size="14"
          class="mr-25"
        />
        Adicionar Envolvido
      </button>
    </div>
  </b-modal>
</template>

<script>
import { BModal, BSpinner } from 'bootstrap-vue'
import InvolvedPersonForm from './InvolvedPersonForm.vue'
import InvolvedVehicleForm from './InvolvedVehicleForm.vue'
import InvolvedObjectForm from './InvolvedObjectForm.vue'
import InvolvedPropertyForm from './InvolvedPropertyForm.vue'
import InvolvedPhoneForm from './InvolvedPhoneForm.vue'

const FORM_COMPONENTS = {
  person: 'InvolvedPersonForm',
  vehicle: 'InvolvedVehicleForm',
  object: 'InvolvedObjectForm',
  property: 'InvolvedPropertyForm',
  phone: 'InvolvedPhoneForm',
}

const LOOKUP_FIELDS = {
  person: { field: 'cpf', label: 'CPF', placeholder: '000.000.000-00' },
  vehicle: { field: 'plate', label: 'Placa', placeholder: 'ABC1D23' },
  phone: { field: 'imei', label: 'IMEI', placeholder: 'IMEI' },
}

function emptyPerson() {
  return {
    unidentified: false,
    name: '',
    apparent_description: '',
    age: null,
    age_unit: 'anos',
    gender: '',
    social_name: '',
    cpf: '',
    rg: '',
    cns: '',
    birth_date: '',
    race: '',
    telephone: '',
    cellphone: '',
  }
}
function emptyVehicle() {
  return {
    unidentified: false,
    plate: '',
    vehicle_type: '',
    color: '',
    brand: '',
    model: '',
    year: '',
    renavam: '',
    chassis: '',
    apparent_description: '',
    observation: '',
  }
}
function emptyObject() {
  return {
    object_type: '',
    description: '',
    brand: '',
    model: '',
    quantity: 1,
    serial_number: '',
    estimated_value: null,
    observation: '',
  }
}
function emptyProperty() {
  return {
    property_type: '', address: '', reference: '', description: '', observation: '',
  }
}
function emptyPhone() {
  return {
    number: '', imei: '', carrier: '', owner_name: '', observation: '',
  }
}

export default {
  name: 'OccurrenceInvolvedModal',
  components: {
    BModal,
    BSpinner,
    InvolvedPersonForm,
    InvolvedVehicleForm,
    InvolvedObjectForm,
    InvolvedPropertyForm,
    InvolvedPhoneForm,
  },

  props: {
    visible: { type: Boolean, default: false },
    involveds: { type: Array, default: () => [] },
    disciplineId: { type: [Number, String], default: null },
  },

  data() {
    return {
      loadingTypes: false,
      entityTypes: [],
      entityType: 'person',
      loadingRoles: false,
      roles: [],
      roleId: null,
      forms: {
        person: emptyPerson(),
        vehicle: emptyVehicle(),
        object: emptyObject(),
        property: emptyProperty(),
        phone: emptyPhone(),
      },
      errors: {},
      lookupLoading: false,
      reuse: null,
    }
  },

  computed: {
    selectableTypes() {
      return this.entityTypes.filter(t => FORM_COMPONENTS[t.code])
    },
    formComponent() {
      return FORM_COMPONENTS[this.entityType] || 'InvolvedPersonForm'
    },
    lookupField() {
      return LOOKUP_FIELDS[this.entityType] || null
    },
    activeForm: {
      get() {
        return this.forms[this.entityType] || this.forms.person
      },
      set(v) {
        this.$set(this.forms, this.entityType, v)
      },
    },
  },

  methods: {
    async onShown() {
      await this.loadEntityTypes()
      await this.loadRoles()
    },
    resetEntry() {
      this.errors = {}
      this.forms = {
        person: emptyPerson(),
        vehicle: emptyVehicle(),
        object: emptyObject(),
        property: emptyProperty(),
        phone: emptyPhone(),
      }
      this.clearLookup()
    },
    clearLookup() {
      this.lookupLoading = false
      this.reuse = null
    },
    emptyFormFor(code) {
      const factories = {
        person: emptyPerson,
        vehicle: emptyVehicle,
        object: emptyObject,
        property: emptyProperty,
        phone: emptyPhone,
      }
      return (factories[code] || emptyPerson)()
    },
    clearReuse() {
      this.reuse = null
      this.$set(this.forms, this.entityType, this.emptyFormFor(this.entityType))
    },
    async selectType(code) {
      if (this.entityType === code) return
      this.entityType = code
      this.roleId = null
      this.errors = {}
      this.clearLookup()
      await this.loadRoles()
    },
    async loadEntityTypes() {
      this.loadingTypes = true
      try {
        const params = this.disciplineId ? { discipline_id: this.disciplineId } : {}
        const res = await this.$http.get(`${this.$store.state.api}involved_entity_types`, { params })
        this.entityTypes = res.data.data || res.data || []
      } catch (e) {
        this.entityTypes = [{ code: 'person', label: 'Pessoa', icon: 'UserIcon' }]
      } finally {
        this.loadingTypes = false
      }
    },
    async loadRoles() {
      this.loadingRoles = true
      this.roles = []
      try {
        const params = { entity_type: this.entityType }
        if (this.disciplineId) params.discipline_id = this.disciplineId
        const res = await this.$http.get(`${this.$store.state.api}involved_roles`, { params })
        this.roles = res.data.data || res.data || []
        if (this.roles.length === 1) this.roleId = this.roles[0].id
      } catch (e) {
        this.roles = []
      } finally {
        this.loadingRoles = false
      }
    },
    // Busca automática ao sair do campo-chave (CPF/placa/IMEI) do formulário.
    async onKeyLookup(value) {
      const key = (value || '').toString().trim()
      if (!key || !this.lookupField || this.reuse) return
      this.lookupLoading = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}involved/lookup`, {
          params: { entity_type: this.entityType, key, key_type: this.lookupField.field },
        })
        const found = res.data.data || null
        if (found) {
          this.fillFormFromLookup(found)
          this.reuse = found
        }
      } catch (e) {
        // silencioso: se a busca falhar, segue como cadastro novo
      } finally {
        this.lookupLoading = false
      }
    },
    fillFormFromLookup(found) {
      const skip = ['entity_type', 'entity_id', 'display_label']
      const form = this.forms[this.entityType]
      Object.keys(found).forEach(k => {
        if (!skip.includes(k) && k in form) this.$set(form, k, found[k])
      })
    },
    typeLabel(code) {
      const t = this.entityTypes.find(x => x.code === code)
      return t ? t.label : code
    },
    roleLabel(id) {
      const r = this.roles.find(x => x.id === id)
      return r ? r.label : ''
    },
    buildDisplayLabel() {
      if (this.reuse) return this.reuse.display_label
      const d = this.activeForm
      if (this.entityType === 'vehicle') {
        return d.plate || [d.brand, d.model].filter(Boolean).join(' ') || d.apparent_description || 'Veículo'
      }
      if (this.entityType === 'object') {
        return d.description || d.object_type || 'Objeto'
      }
      if (this.entityType === 'property') {
        return d.address || d.description || d.property_type || 'Imóvel'
      }
      if (this.entityType === 'phone') {
        return d.number || d.owner_name || 'Celular'
      }
      if (d.unidentified) return d.apparent_description || '—'
      return d.name || '—'
    },
    validate() {
      this.errors = {}
      if (this.roles.length > 0 && !this.roleId) {
        this.$message('Atenção', 'Selecione o papel do envolvido.', 'warning')
        return false
      }
      if (this.reuse) return true
      if (this.entityType === 'person') {
        const f = this.forms.person
        const nameField = f.unidentified ? f.apparent_description : f.name
        if (!nameField || !nameField.trim()) {
          this.$set(this.errors, 'name', true)
          return false
        }
      }
      if (this.entityType === 'object' && (!this.forms.object.description || !this.forms.object.description.trim())) {
        this.$set(this.errors, 'description', true)
        return false
      }
      return true
    },
    addInvolved() {
      if (!this.validate()) return

      const item = {
        entity_type: this.entityType,
        entity_type_label: this.typeLabel(this.entityType),
        involved_role_id: this.roleId,
        role_label: this.roleLabel(this.roleId),
        display_label: this.buildDisplayLabel(),
        entity_id: this.reuse ? this.reuse.entity_id : null,
        entity: this.reuse ? null : { ...this.activeForm },
      }

      this.$emit('update:involveds', [...this.involveds, item])
      this.resetEntry()
      this.entityType = 'person'
      this.roleId = null
      this.loadRoles()
    },
    remove(idx) {
      this.$emit('update:involveds', this.involveds.filter((_, i) => i !== idx))
    },
  },
}
</script>

<style scoped>
.inv-footer {
  border-top: 1px solid #ebe9f1;
  gap: 8px;
  flex-wrap: wrap;
}
.dark-layout .inv-footer {
  border-top-color: #3b4253;
}
</style>
